/**
 * Explorar mesas (docs/spec/explore-rooms.md): no alto, "Salas", a busca e o "Nova mesa"; abaixo, uma
 * fileira com duas chaves ("Com gente agora" e "Só as que não conheço") e os filtros, que abrem painéis
 * (sistema, língua, frequência e "Mais filtros": situação, visual, cobrança e características); depois
 * as mesas, em grade ou em lista, com os mesmos cartões do Início. É uma lista só (decisão do RPG
 * Desk): as recomendadas são a ordenação padrão, "com gente agora" é um filtro e "só as que não
 * conheço" troca as mesas de todas pelas da aba de descobrir do servidor. O servidor devolve junto
 * quantas mesas cada aba teria e os sistemas e as línguas da lista. As suas mesas (recentes, favoritas,
 * minhas) ficam no Início. Na tela estreita, as chaves rolam para o lado, os filtros abrem todos
 * juntos numa folha, pelo "Filtros", e as mesas ficam em cartões, um por linha.
 */
import { useEffect, useId, useMemo, useState } from 'react';
import { useLanguage, useT } from '../i18n/index.js';
import { ROOM_FREQUENCIES, ROOM_TAGS, ROOM_VISUALS, systemKey, type ExploreQueryInput, type ExploreSort, type ExploreTab, type RoomCard } from '../shared.js';
import { useApp } from '../store.js';
import { Button, CheckBox, ComboBox, Segmented, Spinner } from '../ui/controls.js';
import { Icon } from '../ui/icons.js';
import { PageHeader } from '../ui/PageHeader.js';
import { SearchBox } from '../ui/SearchBox.js';
import { NARROW_QUERY, useMediaQuery } from '../ui/mobile.js';
import { CreateRoomPopup } from './CreateRoomPopup.js';
import { FilterMenu } from './FilterMenu.js';
import { RoomOverviewPopup } from './RoomOverviewPopup.js';
import { languageName } from './roomDetails.js';
import { EmptyNote, RoomTile } from './RoomTile.js';
import { useExplore } from './state.js';
import './explore.css';

/** sistemas à vista antes do "Mostrar todos" */
const SYSTEMS_SHOWN = 8;
/** a lista se atualiza sozinha (quem entrou, quem saiu) enquanto a tela está à vista */
const REFRESH_MS = 30_000;
/** a busca espera a pessoa parar de digitar */
const TYPING_MS = 300;
const LAYOUT_KEY = 'rpgdesk.exploreLayout';
type Layout = 'grid' | 'list';
/** característica: só as mesas com ela, ou só as sem ela */
type TagState = 'in' | 'out';
/** cobrança: qualquer mesa, só as gratuitas ou só as pagas */
type Pay = 'any' | 'free' | 'paid';
const toggled = (list: string[], id: string, on: boolean) => (on ? [...list, id] : list.filter((item) => item !== id));

function loadLayout(): Layout {
  try {
    return localStorage.getItem(LAYOUT_KEY) === 'list' ? 'list' : 'grid';
  } catch {
    return 'grid';
  }
}

export function ExploreState({ initialTab = 'online', initialQuery = '', initialSystem }: { initialTab?: ExploreTab; initialQuery?: string; initialSystem?: string }) {
  const t = useT();
  const ui = useLanguage((s) => s.language);
  const hideAdult = useApp((s) => s.settings.hideAdultRooms);
  const sort = useExplore((s) => s.sort);
  const setSort = useExplore((s) => s.setSort);
  const load = useExplore((s) => s.load);
  // as listas de antes chegam como chaves: "com gente agora" e "descobrir"; recomendadas e todas são a lista inteira
  const [liveOnly, setLiveOnly] = useState(initialTab === 'online');
  const [newOnly, setNewOnly] = useState(initialTab === 'discover');
  const tab: ExploreTab = newOnly ? 'discover' : 'search';
  const state = useExplore((s) => s.tabs[tab]);
  const [words, setWords] = useState(initialQuery);
  const [query, setQuery] = useState(initialQuery.trim());
  const [gm, setGm] = useState(false);
  const [noPassword, setNoPassword] = useState(false);
  const [vacancies, setVacancies] = useState(false);
  const [systems, setSystems] = useState<string[]>(initialSystem ? [initialSystem] : []);
  const [tags, setTags] = useState<Partial<Record<string, TagState>>>({});
  const [languages, setLanguages] = useState<string[]>([]);
  const [frequencies, setFrequencies] = useState<string[]>([]);
  const [visuals, setVisuals] = useState<string[]>([]);
  const [pay, setPay] = useState<Pay>('any');
  const [allSystems, setAllSystems] = useState(false);
  const [layout, setLayout] = useState<Layout>(loadLayout);
  const [open, setOpen] = useState<RoomCard | null>(null);
  const [creating, setCreating] = useState(false);
  const narrow = useMediaQuery(NARROW_QUERY);
  const moreId = useId();

  useEffect(() => {
    const timer = window.setTimeout(() => setQuery(words.trim()), TYPING_MS);
    return () => window.clearTimeout(timer);
  }, [words]);

  const tagIds = (wanted: TagState) => Object.keys(tags).filter((id) => tags[id] === wanted);
  const params = useMemo<ExploreQueryInput>(
    () => ({
      query,
      withPlayersOnline: liveOnly,
      withGMOnline: gm,
      withoutPassword: noPassword,
      withVacancies: vacancies,
      withoutAdult: hideAdult,
      systems,
      requiredTags: Object.keys(tags).filter((id) => tags[id] === 'in'),
      notTags: Object.keys(tags).filter((id) => tags[id] === 'out'),
      languages,
      frequencies,
      visuals,
      pay: pay === 'any' ? undefined : pay,
    }),
    [query, liveOnly, gm, noPassword, vacancies, hideAdult, systems, tags, languages, frequencies, visuals, pay],
  );

  useEffect(() => {
    const reload = () => {
      if (document.visibilityState === 'visible') void load(tab, params);
    };
    reload();
    const timer = window.setInterval(reload, REFRESH_MS);
    document.addEventListener('visibilitychange', reload);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', reload);
    };
  }, [load, tab, params, sort]);

  const rooms = state?.rooms ?? [];
  const loading = state?.loading ?? true;
  const total = state?.counts?.[tab] ?? rooms.length;

  // os sistemas da lista, mais os marcados que saíram dela (para dar para desmarcar)
  const isChosen = (name: string) => systems.some((chosen) => systemKey(chosen) === systemKey(name));
  const systemList = useMemo(() => {
    const list = [...(state?.systems ?? [])];
    for (const name of systems) if (!list.some((s) => systemKey(s.name) === systemKey(name))) list.push({ name, count: 0 });
    return list;
  }, [state?.systems, systems]);
  const shownSystems = allSystems ? systemList : systemList.filter((s, index) => index < SYSTEMS_SHOWN || isChosen(s.name));
  // as línguas da lista, mais as marcadas que saíram dela
  const languageList = useMemo(() => {
    const list = [...(state?.languages ?? [])];
    for (const code of languages) if (!list.some((l) => l.code === code)) list.push({ code, count: 0 });
    return list;
  }, [state?.languages, languages]);
  const toggleSystem = (name: string, on: boolean) => setSystems((list) => (on ? [...list, name] : list.filter((chosen) => systemKey(chosen) !== systemKey(name))));
  const cycleTag = (id: string) =>
    setTags((current) => {
      const next = { ...current };
      if (!current[id]) next[id] = 'in';
      else if (current[id] === 'in') next[id] = 'out';
      else delete next[id];
      return next;
    });
  const tagName = (id: string) => t(`roomTag.${id}`);

  // o que está ligado, na ordem da coluna (o resumo do alto e a conta do botão "Filtros")
  const filters = [
    ...(gm ? [t('explore.search.withGMOnline')] : []),
    ...(noPassword ? [t('explore.search.withoutPassword')] : []),
    ...(vacancies ? [t('explore.card.vacancies')] : []),
    ...systems,
    ...languages.map((code) => languageName(code, ui)),
    ...frequencies.map((id) => t(`roomDetails.frequency.${id}`)),
    ...visuals.map((id) => t(`roomDetails.visuals.${id}`)),
    ...(pay !== 'any' ? [t(`explore.facets.pay.${pay}`)] : []),
    ...tagIds('in').map(tagName),
    ...tagIds('out').map((id) => t('explore.tag.without', { name: tagName(id) })),
  ];
  const filtered = filters.length > 0 || query !== '' || liveOnly || newOnly;
  const clear = () => {
    setLiveOnly(false);
    setNewOnly(false);
    setWords('');
    setQuery('');
    setGm(false);
    setNoPassword(false);
    setVacancies(false);
    setSystems([]);
    setLanguages([]);
    setFrequencies([]);
    setVisuals([]);
    setPay('any');
    setTags({});
  };
  const count = t(total === 1 ? 'explore.count.one' : 'explore.count.many', { n: total });
  // a lista vazia: pelos filtros e pela busca, ou pelo que as chaves pedem
  const emptyKind = filters.length > 0 || query !== '' ? 'filtered' : liveOnly ? 'online' : newOnly ? 'discover' : 'search';
  const summary = [count, ...(query ? [`“${query}”`] : []), ...filters].join(' · ');
  const pickLayout = (next: Layout) => {
    setLayout(next);
    try {
      localStorage.setItem(LAYOUT_KEY, next);
    } catch {
      // sem armazenamento: vale só agora
    }
  };

  const statusGroup = (
    <fieldset className="rd-facets__group">
      <legend className="rd-facets__h">{t('explore.facets.status')}</legend>
      <CheckBox checked={gm} onChange={setGm}>
        {t('explore.search.withGMOnline')}
      </CheckBox>
      <CheckBox checked={noPassword} onChange={setNoPassword}>
        {t('explore.search.withoutPassword')}
      </CheckBox>
      <CheckBox checked={vacancies} onChange={setVacancies}>
        {t('explore.card.vacancies')}
      </CheckBox>
    </fieldset>
  );
  const systemGroup = (
    <fieldset className="rd-facets__group">
      <legend className="rd-facets__h">{t('explore.facets.system')}</legend>
      {shownSystems.length ? (
        shownSystems.map((system) => (
          <CheckBox key={systemKey(system.name)} checked={isChosen(system.name)} onChange={(on) => toggleSystem(system.name, on)}>
            <span className="rd-facets__system">
              <span className="rd-facets__systemName">{system.name}</span>
              <span className="rd-facets__count">{system.count}</span>
            </span>
          </CheckBox>
        ))
      ) : (
        <p className="rd-facets__note">{t('explore.facets.noSystems')}</p>
      )}
      {systemList.length > SYSTEMS_SHOWN ? (
        <button type="button" className="rd-facets__link" onClick={() => setAllSystems(!allSystems)}>
          {allSystems ? t('explore.facets.fewerSystems') : t('explore.facets.allSystems', { n: systemList.length })}
        </button>
      ) : null}
    </fieldset>
  );
  const languageGroup = (
    <fieldset className="rd-facets__group">
      <legend className="rd-facets__h">{t('explore.facets.language')}</legend>
      {languageList.length ? (
        languageList.map((language) => (
          <CheckBox key={language.code} checked={languages.includes(language.code)} onChange={(on) => setLanguages((list) => toggled(list, language.code, on))}>
            <span className="rd-facets__system">
              <span className="rd-facets__systemName">{languageName(language.code, ui)}</span>
              <span className="rd-facets__count">{language.count}</span>
            </span>
          </CheckBox>
        ))
      ) : (
        <p className="rd-facets__note">{t('explore.facets.noLanguages')}</p>
      )}
    </fieldset>
  );
  const frequencyGroup = (
    <section className="rd-facets__group" aria-labelledby={`${moreId}-frequency`}>
      <h2 id={`${moreId}-frequency`} className="rd-facets__h">
        {t('explore.facets.frequency')}
      </h2>
      <div className="rd-facets__chips">
        {ROOM_FREQUENCIES.map((id) => (
          <button key={id} type="button" className={`rd-facetChip${frequencies.includes(id) ? ' is-in' : ''}`} aria-pressed={frequencies.includes(id)} onClick={() => setFrequencies((list) => toggled(list, id, !list.includes(id)))}>
            {t(`roomDetails.frequency.${id}`)}
          </button>
        ))}
      </div>
    </section>
  );
  const visualsGroup = (
    <section className="rd-facets__group" aria-labelledby={`${moreId}-visuals`}>
      <h2 id={`${moreId}-visuals`} className="rd-facets__h">
        {t('explore.facets.visuals')}
      </h2>
      <div className="rd-facets__chips">
        {ROOM_VISUALS.map((id) => (
          <button key={id} type="button" className={`rd-facetChip${visuals.includes(id) ? ' is-in' : ''}`} aria-pressed={visuals.includes(id)} onClick={() => setVisuals((list) => toggled(list, id, !list.includes(id)))}>
            {t(`roomDetails.visuals.${id}`)}
          </button>
        ))}
      </div>
    </section>
  );
  const payGroup = (
    <section className="rd-facets__group" aria-labelledby={`${moreId}-pay`}>
      <h2 id={`${moreId}-pay`} className="rd-facets__h">
        {t('explore.facets.pay')}
      </h2>
      <Segmented value={pay} onChange={setPay} options={(['any', 'free', 'paid'] as const).map((id) => ({ value: id, label: t(`explore.facets.pay.${id}`) }))} aria-labelledby={`${moreId}-pay`} />
    </section>
  );
  const tagsGroup = (
    <section className="rd-facets__group" aria-labelledby={`${moreId}-tags`}>
      <h2 id={`${moreId}-tags`} className="rd-facets__h">
        {t('create.tags')}
      </h2>
      <div className="rd-facets__chips">
        {ROOM_TAGS.map((id) => (
          <button key={id} type="button" className={`rd-facetChip${tags[id] ? ` is-${tags[id]}` : ''}`} aria-label={`${tagName(id)}: ${t(`explore.tag.${tags[id] ?? 'any'}`)}`} onClick={() => cycleTag(id)}>
            {tagName(id)}
          </button>
        ))}
      </div>
      <p className="rd-facets__note">{t('explore.facets.tagsHint')}</p>
    </section>
  );
  const clearButton = filtered ? (
    <button type="button" className="rd-facets__link" onClick={clear}>
      {t('explore.facets.clear')}
    </button>
  ) : null;
  // "Mais filtros": a situação, o visual, a cobrança e as características
  const moreCount = (gm ? 1 : 0) + (noPassword ? 1 : 0) + (vacancies ? 1 : 0) + visuals.length + (pay !== 'any' ? 1 : 0) + Object.keys(tags).length;
  const sortBox = (
    <ComboBox<ExploreSort>
      value={sort}
      onChange={setSort}
      aria-label={t('explore.sortLabel')}
      options={[
        { value: 'recommendation', label: t('explore.sort', { mode: t('explore.sort.recommendation') }) },
        { value: 'onlinePlayersQty', label: t('explore.sort', { mode: t('explore.sort.onlinePlayersQty') }) },
      ]}
    />
  );

  return (
    <div className="rd-explore">
      <PageHeader
        className="rd-explore__top"
        titleId={`${moreId}-title`}
        title={t('logged.nav.rooms')}
        actions={
          <>
            <SearchBox className="rd-explore__search" value={words} onChange={setWords} placeholder={t('explore.search.words')} label={t('explore.search.label')} />
            <Button variant="action" icon="plus" className="rd-explore__create" onClick={() => setCreating(true)}>
              {t('explore.newRoom')}
            </Button>
          </>
        }
      />

      <div className="rd-explore__bar">
        <div className="rd-explore__quick" role="group" aria-label={t('explore.nav')}>
          <button type="button" aria-pressed={liveOnly} className={`rd-quickChip${liveOnly ? ' is-on' : ''}`} onClick={() => setLiveOnly(!liveOnly)}>
            <span className="rd-home__dot rd-quickChip__live" aria-hidden="true" />
            {t('explore.tab.online')}
            {state?.counts ? <span className="rd-quickChip__count">{state.counts.online}</span> : null}
          </button>
          <button type="button" aria-pressed={newOnly} className={`rd-quickChip${newOnly ? ' is-on' : ''}`} onClick={() => setNewOnly(!newOnly)}>
            <Icon name={newOnly ? 'check' : 'map'} size={15} />
            {t('explore.only.new')}
          </button>
        </div>
        {narrow ? (
          <div className="rd-explore__filters">
            <FilterMenu
              sheet
              label={t('explore.facets')}
              icon="sliders"
              count={filters.length}
              actions={clearButton}
              done={(close) => (
                <Button variant="action" className="rd-facets__done" onClick={close}>
                  {t('explore.filters.show', { count })}
                </Button>
              )}
            >
              {statusGroup}
              {systemGroup}
              {languageGroup}
              {frequencyGroup}
              {visualsGroup}
              {payGroup}
              {tagsGroup}
            </FilterMenu>
            {sortBox}
          </div>
        ) : (
          <div className="rd-explore__filters">
            <FilterMenu label={t('explore.facets.system')} count={systems.length}>
              {systemGroup}
            </FilterMenu>
            <FilterMenu label={t('explore.facets.language')} count={languages.length}>
              {languageGroup}
            </FilterMenu>
            <FilterMenu label={t('explore.facets.frequency')} count={frequencies.length}>
              {frequencyGroup}
            </FilterMenu>
            <FilterMenu label={t('explore.filters.more')} icon="sliders" count={moreCount}>
              {statusGroup}
              {visualsGroup}
              {payGroup}
              {tagsGroup}
            </FilterMenu>
          </div>
        )}
      </div>

      <main className="rd-explore__main" aria-labelledby={`${moreId}-title`}>
        <div className="rd-explore__head">
          <div className="rd-explore__summary" aria-live="polite">
            <span>{summary}</span>
            {loading && !rooms.length ? <Spinner size={16} /> : null}
            {clearButton}
          </div>
          {narrow ? null : (
            <div className="rd-explore__tools">
              {sortBox}
              <div className="rd-explore__layout" role="group" aria-label={t('explore.layout')}>
                <button type="button" aria-label={t('explore.layout.grid')} aria-pressed={layout === 'grid'} onClick={() => pickLayout('grid')}>
                  <Icon name="grid" size={18} />
                </button>
                <button type="button" aria-label={t('explore.layout.list')} aria-pressed={layout === 'list'} onClick={() => pickLayout('list')}>
                  <Icon name="list" size={18} />
                </button>
              </div>
            </div>
          )}
        </div>

        {rooms.length ? (
          <div className={`rd-roomGrid${!narrow && layout === 'list' ? ' rd-roomGrid--rows' : ''}`}>
            {rooms.map((card) => (
              <RoomTile key={card.id} card={card} label={t('explore.card.open', { name: card.name })} onActivate={() => setOpen(card)} onOpen={setOpen} />
            ))}
          </div>
        ) : !loading ? (
          <EmptyNote icon={emptyKind === 'filtered' ? 'search' : emptyKind === 'online' ? 'clock' : 'die'} title={t(`explore.empty.${emptyKind}`)} text={t(`explore.empty.${emptyKind}.text`)} wide>
            {emptyKind === 'filtered' ? (
              <Button variant="secondary" onClick={clear}>
                {t('explore.facets.clear')}
              </Button>
            ) : null}
            {emptyKind !== 'filtered' && (liveOnly || newOnly ? (
              <Button variant="secondary" onClick={clear}>
                {t('explore.empty.all')}
              </Button>
            ) : null)}
            <Button variant="action" onClick={() => setCreating(true)}>
              {t('explore.empty.create')}
            </Button>
          </EmptyNote>
        ) : null}
      </main>

      {open ? <RoomOverviewPopup card={rooms.find((r) => r.id === open.id) ?? open} onClose={() => setOpen(null)} /> : null}
      {creating ? <CreateRoomPopup onClose={() => setCreating(false)} /> : null}
    </div>
  );
}
